import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../config/firebase.config';
import { v4 as uuidv4 } from 'uuid';

// Servicio para manejar la subida de archivos a Firebase Storage
export const FirebaseStorageService = {
  // Subir un archivo y obtener la URL de descarga
  uploadFile: async (file: File, folder: string = 'general', userId?: string): Promise<string> => {
    try {
      // Obtener el ID del usuario actual o usar el proporcionado
      const currentUser = auth.currentUser;
      const uid = userId || (currentUser ? currentUser.uid : 'anonymous');

      // Crear un nombre único para el archivo
      const fileExtension = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExtension}`;

      // Crear la referencia al archivo en Firebase Storage usando la estructura de carpetas correcta
      let path;
      if (currentUser && currentUser.isAnonymous) {
        // Usuario anónimo en onboarding
        path = `onboarding/${uid}/${folder}/${fileName}`;
      } else if (currentUser) {
        // Usuario autenticado normal
        path = `users/${uid}/${folder}/${fileName}`;
      } else {
        // Fallback para casos excepcionales
        path = `public/${folder}/${fileName}`;
      }

      const storageRef = ref(storage, path);

      // Definir metadatos para resolver problemas CORS
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
          'Access-Control-Allow-Headers': 'Content-Type, Content-Length, Content-Disposition'
        }
      };

      // Subir el archivo con metadatos
      const snapshot = await uploadBytes(storageRef, file, metadata);

      // Obtener la URL de descarga
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      throw error;
    }
  },

  // Subir una imagen en base64 y obtener la URL de descarga
  uploadBase64Image: async (base64String: string, folder: string = 'images'): Promise<string> => {
    try {
      // Extraer solo la parte base64 si incluye el prefijo data:image
      let base64Content = base64String;
      if (base64String.includes(',')) {
        base64Content = base64String.split(',')[1];
      }

      // Convertir base64 a blob
      const byteCharacters = atob(base64Content);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, { type: 'image/jpeg' });

      // Crear un nombre único para el archivo
      const fileName = `${uuidv4()}.jpg`;

      // Crear la referencia al archivo en Firebase Storage
      const storageRef = ref(storage, `${folder}/${fileName}`);

      // Definir metadatos para resolver problemas CORS
      const metadata = {
        contentType: 'image/jpeg',
        customMetadata: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
          'Access-Control-Allow-Headers': 'Content-Type, Content-Length, Content-Disposition'
        }
      };

      // Subir el archivo con metadatos
      const snapshot = await uploadBytes(storageRef, blob, metadata);

      // Obtener la URL de descarga
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      throw error;
    }
  },

  // Subir un documento en base64 y obtener la URL de descarga
  uploadBase64Document: async (base64String: string, fileName: string, folder: string = 'documents'): Promise<string> => {
    try {
      // Extraer solo la parte base64 si incluye el prefijo data:application
      let base64Content = base64String;
      if (base64String.includes(',')) {
        base64Content = base64String.split(',')[1];
      }

      // Determinar el tipo de archivo basado en la extensión
      const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
      let mimeType = 'application/octet-stream'; // Tipo por defecto

      // Asignar el tipo MIME correcto según la extensión
      switch (fileExtension) {
        case 'pdf':
          mimeType = 'application/pdf';
          break;
        case 'doc':
        case 'docx':
          mimeType = 'application/msword';
          break;
        case 'xls':
        case 'xlsx':
          mimeType = 'application/vnd.ms-excel';
          break;
        case 'jpg':
        case 'jpeg':
          mimeType = 'image/jpeg';
          break;
        case 'png':
          mimeType = 'image/png';
          break;
        default:
          mimeType = 'application/octet-stream';
      }

      // Convertir base64 a blob
      const byteCharacters = atob(base64Content);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, { type: mimeType });

      // Crear un nombre único para el archivo manteniendo la extensión original
      const uniqueFileName = `${uuidv4()}.${fileExtension}`;

      // Crear la referencia al archivo en Firebase Storage
      const storageRef = ref(storage, `${folder}/${uniqueFileName}`);

      // Definir metadatos para resolver problemas CORS
      const metadata = {
        contentType: mimeType,
        customMetadata: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
          'Access-Control-Allow-Headers': 'Content-Type, Content-Length, Content-Disposition'
        }
      };

      // Subir el archivo con metadatos
      const snapshot = await uploadBytes(storageRef, blob, metadata);

      // Obtener la URL de descarga
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      throw error;
    }
  }
};

export default FirebaseStorageService;
